/**
 * components/KelolaKategoriModal.js
 * Manage the categories list: add new, rename (name always, code only when unused),
 * delete (only when empty). Mirrors the MobilPage management pattern.
 */
import React, { useMemo, useState } from "react";
import { suggestCategoryCode, validateCategoryCode } from "../utils/categoryCode";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { PencilIcon, TrashIcon } from "./Icons";
import { useModalEsc, backdropClose } from "../utils/modalStack";

export default function KelolaKategoriModal({
  categories, items, onCreateCategory, onRenameCategory, onDeleteCategory, onOpenCategory, onClose,
}) {
  const [addingCat, setAddingCat] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newCodeTouched, setNewCodeTouched] = useState(false);

  const [editingCat, setEditingCat] = useState(null); // { name, code } being edited
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null); // category name string

  // The `!deleteTarget` guard the old hand-rolled listener needed is gone on purpose —
  // `DeleteConfirmModal` (rendered nested, below) already uses `useModalEsc` itself, so the
  // shared stack closes IT first while it's open; this modal's own Esc only fires once nothing
  // is stacked on top of it.
  useModalEsc(onClose);

  // Count map: category name.toLowerCase() → item count
  const countMap = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const key = (it.category || "").trim().toLowerCase();
      if (key) m.set(key, (m.get(key) || 0) + 1);
    }
    return m;
  }, [items]);

  const getCount = (name) => countMap.get((name || "").trim().toLowerCase()) || 0;

  const existingCodes = categories.map((c) => c.code);

  // ── Add panel ──────────────────────────────────────────────────────────────
  const tidyName = (s) => String(s || "").trim().replace(/\s+/g, " ");
  const nmAdd = tidyName(newName);
  // Disabled-control sweep (manual-testing report): a blank name used to return "" here — unlike
  // this same panel's own `codeErrorAdd` (`validateCategoryCode`, already says "wajib diisi" for
  // blank) and the EDIT panel's own `editNameError` below (already says "Nama wajib diisi."}) —
  // so the ADD panel's Tambah sat disabled with nothing explaining why for this one specific
  // field. Flows through the existing `{nameErrorAdd && ...}` hint, no new JSX needed.
  const nameErrorAdd = !nmAdd
    ? "Wajib diisi."
    : categories.some((c) => tidyName(c.name).toLowerCase() === nmAdd.toLowerCase())
      ? "Nama kategori sudah ada."
      : "";
  const codeErrorAdd = validateCategoryCode(newCode, existingCodes);

  const handleAddName = (e) => {
    const v = e.target.value;
    setNewName(v);
    if (!newCodeTouched) setNewCode(suggestCategoryCode(v, existingCodes));
  };
  const handleAddCode = (e) => { setNewCodeTouched(true); setNewCode(e.target.value.toUpperCase()); };

  const collapseAdd = () => {
    setAddingCat(false); setNewName(""); setNewCode(""); setNewCodeTouched(false);
  };

  const handleTambah = () => {
    if (!nmAdd || nameErrorAdd || codeErrorAdd) return;
    onCreateCategory(nmAdd, newCode.trim().toUpperCase());
    collapseAdd();
  };

  // ── Edit panel ────────────────────────────────────────────────────────────
  const openEdit = (cat) => {
    collapseAdd();
    setEditingCat(cat);
    setEditName(cat.name);
    setEditCode(cat.code);
  };

  const collapseEdit = () => { setEditingCat(null); setEditName(""); setEditCode(""); };

  const editCount = editingCat ? getCount(editingCat.name) : 0;
  const editUsed = editCount > 0;

  // Codes for edit validation: all except the current category's own code.
  const codesExceptSelf = editingCat
    ? categories.filter((c) => c.name.toLowerCase() !== (editingCat.name || "").toLowerCase()).map((c) => c.code)
    : [];

  const editNameTrimmed = tidyName(editName);
  const editNameError = !editNameTrimmed
    ? "Nama wajib diisi."
    : editNameTrimmed.toLowerCase() !== tidyName(editingCat?.name || "").toLowerCase() &&
      categories.some((c) => tidyName(c.name).toLowerCase() === editNameTrimmed.toLowerCase())
    ? "Nama kategori sudah dipakai."
    : "";
  const editCodeError = editUsed ? "" : validateCategoryCode(editCode, codesExceptSelf);
  const editValid = !!editNameTrimmed && !editNameError && (editUsed || !editCodeError);

  const handleSimpan = () => {
    if (!editValid || !editingCat) return;
    onRenameCategory(
      editingCat.name,
      editNameTrimmed,
      editUsed ? undefined : editCode.trim().toUpperCase()
    );
    collapseEdit();
  };

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Kelola Kategori</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
            Tambah, ubah nama, atau hapus kategori. Kategori mengelompokkan barang dan menentukan kode internalnya.
          </div>

          {/* + Tambah Kategori trigger / panel */}
          {!addingCat && (
            <div style={{ marginBottom: "var(--gap-lg)" }}>
              <button type="button" className="btn btn-soft"
                onClick={() => { collapseEdit(); setAddingCat(true); }}>
                <span className="btn-circle-plus" aria-hidden="true">+</span>
                Tambah Kategori
              </button>
            </div>
          )}
          {addingCat && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 12, marginBottom: "var(--gap-lg)" }}>
              <div className="form-row">
                <label className="form-label" htmlFor="kelola-kategori-add-nama">Nama kategori</label>
                <input id="kelola-kategori-add-nama" className="form-input" value={newName} autoFocus onChange={handleAddName} />
                {nameErrorAdd && <div className="form-hint" style={{ color: "var(--red)" }}>{nameErrorAdd}</div>}
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="kelola-kategori-add-kode">Kode</label>
                <input id="kelola-kategori-add-kode" className="form-input mono" value={newCode} onChange={handleAddCode} />
                {codeErrorAdd && <div className="form-hint" style={{ color: "var(--red)" }}>{codeErrorAdd}</div>}
              </div>
              <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                <button type="button" className="btn" onClick={collapseAdd}>Batal</button>
                <button type="button" className="btn btn-primary"
                  disabled={!nmAdd || !!nameErrorAdd || !!codeErrorAdd}
                  onClick={handleTambah}>Tambah</button>
              </div>
            </div>
          )}

          {/* Category table */}
          <div className="table-wrap kelola-table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kategori</th>
                  <th>Kode</th>
                  <th>Jumlah Barang</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">Belum ada kategori.</td></tr>
                ) : (
                  categories.map((cat) => {
                    const count = getCount(cat.name);
                    const isEditing = editingCat?.name === cat.name;
                    return (
                      <React.Fragment key={cat.name}>
                        <tr style={{ cursor: "pointer" }}
                          onClick={() => !isEditing && onOpenCategory(cat)}>
                          <td>{cat.name}</td>
                          <td><span className="chip chip--muted">{cat.code}</span></td>
                          <td>{count}</td>
                          <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                            <button className="btn btn-sm btn-ghost"
                              onClick={() => isEditing ? collapseEdit() : openEdit(cat)}>
                              <PencilIcon /> Ubah
                            </button>
                            <span
                              className="tip"
                              {...(count > 0 ? { "data-tip": `Dipakai oleh ${count} barang — pindahkan atau hapus barang dulu.` } : {})}>
                              <button className="btn btn-sm btn-danger-ghost"
                                disabled={count > 0}
                                onClick={() => setDeleteTarget(cat.name)}>
                                <TrashIcon /> Hapus
                              </button>
                            </span>
                          </td>
                        </tr>
                        {isEditing && (
                          <tr>
                            <td colSpan={4} style={{ background: "#f8fafc" }}>
                              <div style={{ padding: "10px 4px" }}>
                                <div className="form-row">
                                  <label className="form-label" htmlFor="kelola-kategori-edit-nama">Nama kategori</label>
                                  <input id="kelola-kategori-edit-nama" className="form-input" value={editName} autoFocus
                                    onChange={(e) => setEditName(e.target.value)} />
                                  {editNameError && (
                                    <div className="form-hint" style={{ color: "var(--red)" }}>{editNameError}</div>
                                  )}
                                </div>
                                <div className="form-row">
                                  <label className="form-label" htmlFor="kelola-kategori-edit-kode">Kode</label>
                                  {editUsed ? (
                                    <>
                                      <input id="kelola-kategori-edit-kode" className="form-input mono" value={editCode} readOnly />
                                      <div className="form-hint">
                                        Kode tidak bisa diubah karena sudah ada barang memakai kategori ini.
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <input id="kelola-kategori-edit-kode" className="form-input mono" value={editCode}
                                        onChange={(e) => setEditCode(e.target.value.toUpperCase())} />
                                      {editCodeError && (
                                        <div className="form-hint" style={{ color: "var(--red)" }}>{editCodeError}</div>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                                  <button type="button" className="btn" onClick={collapseEdit}>Batal</button>
                                  <button type="button" className="btn btn-primary"
                                    disabled={!editValid}
                                    onClick={handleSimpan}>Simpan</button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="modal-actions" style={{ marginTop: "var(--gap-lg)" }}>
            <button type="button" className="btn btn-primary" onClick={onClose}>Tutup</button>
          </div>
        </div>
      </div>

      {/* Delete confirm rendered as sibling of the main overlay so it stacks on top correctly. */}
      {deleteTarget !== null && (
        <DeleteConfirmModal
          title="Hapus kategori?"
          message={<>Anda akan menghapus kategori <b>"{deleteTarget}"</b>. Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => { onDeleteCategory(deleteTarget); setDeleteTarget(null); }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
